/* eslint-disable react/no-danger */
import { lazy, Suspense, useEffect, useState } from 'react';
import { useIsGlobetrotter } from '@bwoty-web/ui-kit/hooks';
import PropTypes from 'prop-types';
import cx from 'classnames';
import uniqid from 'uniqid';
import { TypographyWrapper, BounceLoader } from '@bwoty-web/ui-kit';
import SectionParagraph from '../../../common/sectionParagraph';
import WeatherChart from '../../../common/weather/weatherChart';
import AccommodationConceptContent from './accommodation/concept/accommodationConceptContent';
import MemoizedAlternativeContentTabWrapper from './alternativeContent/alternativeContentTabWrapper';
import { sectionKeys } from './helper';
import AccommodationContent from './accommodation/accommodationContent';
import './sectionContentContainer.scss';
import InspirationalImages from '../components/inspirationalImages';
import AccessibilityContent from './accessibility/accessibilityContent';

const MapContentResort = lazy(
  () =>
    import(
      /* webpackChunkName: "map-content-resort" */ '../../../common/map/MapContentResort'
    ),
);
const SustainabilityContent = lazy(
  () =>
    import(
      /* webpackChunkName: "sustainability" */ './sustainability/sustainabilityContent'
    ),
);
const MemoizedHotelReviewContent = lazy(
  () =>
    import(
      /* webpackChunkName: "hotel-reviews" */ './hotelReviews/hotelReviews'
    ),
);

const SectionContentContainer = ({
  labels,
  section,
  siteSettings,
  wvId,
  toggleRoomInfo,
  isActive,
  selectedSubSection = 0,
  changeSelectedSection,
  nonConceptAccommodation = null,
}) => {
  const isGlobe = useIsGlobetrotter();
  const [canReadPartialPages, setCanReadPartialPages] = useState(false);
  const mergedLabels = { ...labels, ...section.labels };

  useEffect(() => {
    if (section.alternativeContentExists) {
      setCanReadPartialPages(true);
    }
  }, [section.alternativeContentExists]);

  const getAccommodationContent = () => {
    const { content, alternativeContentExists, bodyArray } = section;

    // Do we ever have alternativeContent for accommodation?
    if (alternativeContentExists) {
      if (canReadPartialPages) {
        const partialPageElement = document.getElementById(
          `hotel-sections-view-partial-page-${section.key}`,
        );
        if (partialPageElement) {
          return (
            <div
              dangerouslySetInnerHTML={{ __html: partialPageElement.innerHTML }}
            />
          );
        }
        return null;
      }
      return null;
    }

    // Concept hotels
    if (bodyArray && bodyArray.length > 0) {
      return (
        <AccommodationConceptContent
          conceptContent={bodyArray}
          labels={mergedLabels}
          toggleRoomInfo={toggleRoomInfo}
          selectedSubSection={selectedSubSection}
        />
      );
    }

    // Hotels with room type content
    if (
      nonConceptAccommodation?.roomTypes?.length > 0 &&
      nonConceptAccommodation.roomTypes.some(
        (i) => i.accommodationSupplier === 'own',
      )
    ) {
      return (
        <AccommodationContent
          nonConceptAccommodation={nonConceptAccommodation}
          siteSettings={siteSettings}
          labels={mergedLabels}
          toggleRoomInfo={toggleRoomInfo}
          accommodationSection={section}
        />
      );
    }

    // Other hotels
    const combinedSubSections = content.reduce((acc, curr) => {
      if (!acc) return curr;
      return { ...acc, paragraphs: [...acc.paragraphs, curr] };
    }, null);

    return (
      <div>
        {combinedSubSections?.paragraphs?.map((paragraph) => {
          return (
            <SectionParagraph
              paragraph={paragraph}
              key={uniqid('section-paragraph-')}
            />
          );
        })}
      </div>
    );
  };

  const getAccessibilityContent = () => {
    const { content } = section;
    const [_, ...rest] = content;

    return <AccessibilityContent content={rest} />;
  };

  const getWeatherContent = () => {
    const { climate, climateWeatherText } = section;

    const isClimateEmpty = (climateWeather) => {
      const noRainfreeDays = climateWeather.rainfreeDays === null;
      const noSunshineHours = climateWeather.sunshineHours === null;
      const noDayTemperature = !climateWeather.temperature?.day?.length;
      const noNightTemperature = !climateWeather.temperature?.night?.length;
      const noWaterTemperature = !climateWeather.temperature?.water?.length;

      return (
        noRainfreeDays &&
        noSunshineHours &&
        noDayTemperature &&
        noNightTemperature &&
        noWaterTemperature
      );
    };

    if (isClimateEmpty(climate)) {
      return (
        <div>
          {climateWeatherText?.map((paragraph) => (
            <SectionParagraph
              paragraph={paragraph}
              key={uniqid('section-paragraph-')}
            />
          )) || null}
        </div>
      );
    }

    return (
      <WeatherChart
        climate={climate}
        siteId={siteSettings?.siteId}
        currentMonth={new Date().getMonth()}
        texts={mergedLabels}
        tightNavPosition={!isGlobe}
      />
    );
  };

  const getMapContent = () => {
    const resortId = section?.geographical?.resort?.id;

    return (
      <Suspense fallback={<BounceLoader />}>
        <MapContentResort
          siteId={siteSettings?.siteId}
          resortId={resortId}
          selectedMarkerWvId={wvId}
          isExpanded={isActive}
          texts={mergedLabels}
          baseUrl={siteSettings?.baseUrl}
          imageDomain={siteSettings?.imageDomain}
          close={() => changeSelectedSection(sectionKeys.overview)}
        />
      </Suspense>
    );
  };

  const getRatingContent = () => {
    const { reviews, ratings } = section;
    return (
      <Suspense fallback={<BounceLoader />}>
        <MemoizedHotelReviewContent
          ratings={ratings}
          reviews={reviews}
          texts={mergedLabels}
          selectedSubSection={selectedSubSection}
          changeSelectedSection={changeSelectedSection}
        />
      </Suspense>
    );
  };

  const getSustainabilityContent = () => {
    const { sustainability, productType } = section;
    return (
      <Suspense fallback={<BounceLoader />}>
        <SustainabilityContent
          sustainability={sustainability}
          productType={productType?.toLowerCase()}
        />
      </Suspense>
    );
  };

  const getDefaultContent = () => {
    const {
      sectionImages,
      content,
      specialParagraphs,
      alternativeContentExists,
    } = section;

    if (alternativeContentExists) {
      if (canReadPartialPages) {
        const partialPageElement = document.getElementById(
          `hotel-sections-view-partial-page-${section.key}`,
        );
        if (!partialPageElement) {
          const partialPageTabs = document.getElementById(
            `hotel-sections-view-partial-tabpages-${section.key}`,
          );
          if (partialPageTabs) {
            const partialPageTabsElements = [
              ...partialPageTabs.querySelectorAll('[class*="tabpage"]'),
            ];
            const alternativeContent = [];
            partialPageTabsElements.forEach((partialPageTabElement) => {
              alternativeContent.push({
                name: partialPageTabElement.id.replace(
                  'hotel-sections-view-partial-tabpage-',
                  '',
                ),
                html: partialPageTabElement.innerHTML,
              });
            });
            return (
              <MemoizedAlternativeContentTabWrapper
                sectionKey={section.key}
                alternativeContent={alternativeContent}
                labels={mergedLabels}
                selectedSubSection={selectedSubSection}
              />
            );
          }
        }
        if (partialPageElement) {
          return (
            <div
              dangerouslySetInnerHTML={{ __html: partialPageElement.innerHTML }}
            />
          );
        }
      }
      return null;
    }

    const [, ...rest] = content;
    const bottomSpecialParagraphs = specialParagraphs?.filter(
      (i) => i && !i.top,
    );

    return (
      <>
        {!!sectionImages?.length && isGlobe && (
          <InspirationalImages
            id={wvId}
            category={section.key}
            images={sectionImages}
            imagesCount={sectionImages.length}
            labels={mergedLabels}
            section
            mapOnClick={() => changeSelectedSection(sectionKeys.map)}
          />
        )}
        {rest?.map((block, i) => {
          return (
            <div
              className={cx('gw-section-block', {
                'gw-section-subsection': block?.type === 'subsection',
              })}
              key={uniqid(i)}
            >
              <h2 className="gw-section-block-heading">{block.heading}</h2>
              <div className="gw-section-block-content">
                {block.paragraphs?.map((paragraph) => {
                  return (
                    <SectionParagraph
                      className={cx({
                        'gw-section-content__paragraph-list':
                          paragraph?.listItems,
                      })}
                      paragraph={paragraph}
                      key={uniqid('section-paragraph-')}
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
        {bottomSpecialParagraphs?.map((bottomParagraph) => (
          <div
            className="gw-section-content--bottom"
            key={uniqid('section-paragraph-')}
          >
            <SectionParagraph paragraph={bottomParagraph} />
          </div>
        ))}
      </>
    );
  };

  const getTopContent = () => {
    const simpleHeadingSections = [
      'weather',
      'map',
      'rating',
      'sustainability',
    ];
    if (!isGlobe) simpleHeadingSections.push('accommodation');

    if (
      simpleHeadingSections.includes(section.key) ||
      section.alternativeContentExists
    ) {
      return <h2 className="gw-section-heading">{section.heading}</h2>;
    }
    const { content, specialParagraphs } = section;
    const [firstBlock] = content;
    const { paragraphs } = firstBlock;

    let leftContent = paragraphs;
    let rightContent = null;
    let specialParagraph = null;

    if (isGlobe) {
      const lastParagraph = paragraphs?.[paragraphs.length - 1];
      specialParagraph = specialParagraphs?.find((i) => i && i.top);
      leftContent =
        !specialParagraph && isGlobe && lastParagraph?.listItems
          ? paragraphs.slice(0, paragraphs.length - 1)
          : paragraphs;
      rightContent =
        specialParagraph || (lastParagraph?.listItems && lastParagraph);
    }

    return (
      <div
        className={cx('gw-section-content--top', {
          'gw-section-content--top--grid': rightContent,
        })}
      >
        <div className="gw-section-content--top-left">
          <h2 className="gw-section-heading">{section.heading}</h2>
          {leftContent?.map((paragraph) => {
            return (
              <SectionParagraph
                className={cx({
                  'gw-section-content__paragraph-list': paragraph?.listItems,
                })}
                paragraph={paragraph}
                key={uniqid('section-paragraph-')}
              />
            );
          })}
        </div>
        {rightContent && (
          <div
            className={cx('gw-section-content--top-right', {
              'gw-section-content--top-right--box': specialParagraph,
            })}
          >
            <SectionParagraph paragraph={rightContent} />
          </div>
        )}
      </div>
    );
  };

  const getContent = () => {
    const nonInitRenderSections = [
      'weather',
      'map',
      'rating',
      'sustainability',
    ];

    // bow out early for pushes not needed for SEO unless they are expanded
    if (nonInitRenderSections.includes(section.key) && !isActive) {
      return null;
    }

    switch (section.key) {
      case 'accommodation': {
        return isGlobe ? getDefaultContent() : getAccommodationContent();
      }
      case 'accessibility': {
        return getAccessibilityContent();
      }
      case 'weather': {
        return getWeatherContent();
      }
      case 'map': {
        return <div className="gw-map-placeholder">{getMapContent()}</div>;
      }
      case 'rating': {
        return getRatingContent();
      }
      case 'sustainability': {
        return getSustainabilityContent();
      }
      default: {
        return getDefaultContent();
      }
    }
  };

  const topContent = getTopContent();
  const content = getContent();

  if (!topContent || !content) {
    return null;
  }

  return (
    <TypographyWrapper
      id={section.key}
      aria-labelledby={`tab-${section.key}`}
      role="tabpanel"
      className={cx(
        'gw-section-content',
        `gw-section-content--${section.key}`,
        {
          'gw-section-content--alternative': section.alternativeContentExists,
          'gw-section-content--hidden': !isActive,
        },
      )}
    >
      {topContent}
      {content}
    </TypographyWrapper>
  );
};

SectionContentContainer.propTypes = {
  section: PropTypes.shape({
    heading: PropTypes.string,
    key: PropTypes.string,
    content: PropTypes.arrayOf(
      PropTypes.shape({
        paragraphs: PropTypes.arrayOf(PropTypes.shape({})),
      }),
    ),
    location: PropTypes.shape({
      longitude: PropTypes.number,
      latitude: PropTypes.number,
    }),
    specialParagraphs: PropTypes.arrayOf(PropTypes.shape({})),
    imageCategories: PropTypes.arrayOf(PropTypes.string),
    sectionImages: PropTypes.arrayOf(
      PropTypes.shape({
        cloudinaryUriTemplate: PropTypes.string,
      }),
    ),
    labels: PropTypes.shape({}),
    alternativeContentExists: PropTypes.bool,
    climate: PropTypes.shape({}),
    climateWeatherText: PropTypes.arrayOf(PropTypes.shape({})),
    name: PropTypes.string,
    meta: PropTypes.shape({
      url: PropTypes.string,
    }),
    geographical: PropTypes.shape({
      resort: PropTypes.shape({
        id: PropTypes.number,
      }),
    }),
    ratings: PropTypes.shape({}),
    sustainability: PropTypes.shape({}),
    reviews: PropTypes.arrayOf(PropTypes.shape({})),
    bodyArray: PropTypes.arrayOf(PropTypes.shape({})),
    productType: PropTypes.string,
  }).isRequired,
  siteSettings: PropTypes.shape({
    siteId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
      .isRequired,
    baseUrl: PropTypes.string.isRequired,
    imageDomain: PropTypes.string.isRequired,
  }).isRequired,
  wvId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  toggleRoomInfo: PropTypes.func.isRequired,
  isActive: PropTypes.bool.isRequired,
  selectedSubSection: PropTypes.number,
  changeSelectedSection: PropTypes.func.isRequired,
  nonConceptAccommodation: PropTypes.shape({
    roomTypes: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  labels: PropTypes.shape({}).isRequired,
};

export default SectionContentContainer;
